import { clientTierLabel, isClientTier } from "@klarity/schema";
import { Link, Outlet } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import { Button, buttonClass } from "../../components/ui/Button";
import { Chip, EmptyState, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { can, useMe } from "../../lib/auth";
import { useSync } from "../gorelo/useSync";
import { useClient } from "./client-ui";

const TAB = "-mb-px border-b-2 border-transparent py-2 text-base text-ink-2 hover:text-ink";
const ACTIVE = { className: "border-green! font-semibold text-ink!" };
const count = (n: number): ReactNode => <span className="ml-1.5 text-xs font-normal text-ink-3">{n}</span>;

/**
 * The client page (redesign spec §5.2): the name, then the six tabs. Its actions, Sync and New document, sit at
 * the right of the name (page frame spec §5.3). The header is kept low so a tab's content, the diagram above all,
 * starts high on the screen: the name is small and the counts are on the tabs only.
 * The tier is a read-only chip beside the name; it is set on the Clients page.
 */
export function ClientBinder({ clientId }: { clientId: string }) {
  const me = useMe();
  const { client, isLoading } = useClient(clientId);
  const { busy, syncClient } = useSync();
  const bar = (
    <PageBar>
      <Crumb>
        <Link to="/clients" activeOptions={{ exact: true }}>
          Clients
        </Link>
      </Crumb>
      {client ? <Crumb current>{client.name}</Crumb> : null}
    </PageBar>
  );
  if (isLoading)
    return (
      <>
        {bar}
        <Page>
          <Spinner label="Loading client…" />
        </Page>
      </>
    );
  if (!client)
    return (
      <>
        {bar}
        <Page>
          <EmptyState title="This client does not exist or you don't have access to it" />
        </Page>
      </>
    );
  const canSync = client.synced && can(me, "edit");
  const canCreate = can(me, "create");
  const params = { clientId };
  return (
    <>
      {bar}
      <Page className="flex flex-col gap-3">
        <header className="-mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-xl font-semibold text-ink">{client.name}</h1>
          {isClientTier(client.tier) ? <Chip>{clientTierLabel(client.tier)}</Chip> : null}
          {client.synced ? null : <span className="text-xs text-ink-3">Not linked to Gorelo</span>}
          {client.restricted ? (
            <span className="inline-flex items-center gap-1 text-xs text-ink-3">
              <Lock size={11} aria-hidden="true" /> Restricted
            </span>
          ) : null}
          {canSync || canCreate ? (
            <span className="ml-auto flex flex-wrap items-center gap-2">
              {canSync ? (
                <Button size="sm" disabled={busy} onClick={() => void syncClient(client.id)}>
                  {busy ? "Syncing…" : "Sync"}
                </Button>
              ) : null}
              {canCreate ? (
                <Link to="/new" search={{ space: client.id }} className={buttonClass({ size: "sm" })}>
                  New document
                </Link>
              ) : null}
            </span>
          ) : null}
        </header>
        <nav aria-label="Client binder tabs" className="flex flex-wrap gap-x-6 border-b border-line">
          <Link
            to="/clients/$clientId"
            params={params}
            activeOptions={{ exact: true }}
            className={TAB}
            activeProps={ACTIVE}
          >
            Overview
          </Link>
          <Link to="/clients/$clientId/documents" params={params} className={TAB} activeProps={ACTIVE}>
            Documents{count(client.counts.documents)}
          </Link>
          <Link to="/clients/$clientId/sites" params={params} className={TAB} activeProps={ACTIVE}>
            Sites{count(client.counts.sites)}
          </Link>
          <Link to="/clients/$clientId/devices" params={params} className={TAB} activeProps={ACTIVE}>
            Devices{count(client.counts.devices)}
          </Link>
          <Link to="/clients/$clientId/network" params={params} className={TAB} activeProps={ACTIVE}>
            Network and printers{count(client.counts.network)}
          </Link>
          {client.synced ? (
            <Link to="/clients/$clientId/diagram" params={params} className={TAB} activeProps={ACTIVE}>
              Diagram
            </Link>
          ) : null}
        </nav>
        <Outlet />
      </Page>
    </>
  );
}
